import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive"
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff"
import { Button, Tooltip, Box, CircularProgress } from "@mui/material"
import { ReactElement, useEffect, useState } from "react"

import { useGetUserData } from "../../../hooks/useGetUserData"
import useDeviceSubscription from "../../../services/middleware/hooks/useDeviceSubscription"

interface ISubscriptionButtonProps {
    deviceSysRef: string
    deviceType?: string
    initialIsSubscribed: boolean
    size?: "small" | "medium" | "large"
    onSubscriptionChange?: (isSubscribed: boolean) => void
}

const SubscriptionButton = ({
    deviceSysRef,
    deviceType,
    initialIsSubscribed,
    size = "large",
    onSubscriptionChange,
}: ISubscriptionButtonProps): ReactElement => {
    const { isEnaSysAdmin, isDnoApproved } = useGetUserData()
    const [isSubscribed, setIsSubscribed] =
        useState<boolean>(initialIsSubscribed)

    // Sync with initialIsSubscribed when it changes
    useEffect(() => {
        setIsSubscribed(initialIsSubscribed)
    }, [initialIsSubscribed])

    // Use our custom hook to manage subscription state and API calls
    const { toggleSubscription, isLoading } = useDeviceSubscription({
        deviceSysRef,
        deviceType,
        isSubscribed,
        setIsSubscribed,
    })

    useEffect(() => {
        if (onSubscriptionChange) {
            onSubscriptionChange(isSubscribed)
        }
    }, [isSubscribed, onSubscriptionChange])

    const getButtonIcon = (): ReactElement => {
        if (isLoading) return <CircularProgress size={16} />
        return isSubscribed ? (
            <NotificationsActiveIcon />
        ) : (
            <NotificationsOffIcon />
        )
    }

    const getButtonText = (): string => {
        if (isLoading) return "Processing..."
        return isSubscribed ? "Subscribed" : "Subscribe"
    }

    const getAriaLabel = (): string =>
        isSubscribed
            ? "Unsubscribe from notifications"
            : "Subscribe to notifications"

    // Create explicit handler function for better debugging
    const handleSubscriptionToggle = (): void => {
        toggleSubscription()
        setIsSubscribed((prev) => !prev)
    }

    // Conditionally render button directly or wrapped in span based on loading state
    const buttonElement = (
        <Button
            onClick={handleSubscriptionToggle}
            color="primary"
            size={size}
            variant="text"
            startIcon={getButtonIcon()}
            aria-label={getAriaLabel()}
            sx={{ textTransform: "none" }}
            disabled={isLoading}
        >
            {getButtonText()}
        </Button>
    )

    return (
        <Box>
            {isEnaSysAdmin || isDnoApproved ? (
                <Tooltip title={getAriaLabel()} arrow>
                    <span>{buttonElement}</span>
                </Tooltip>
            ) : null}
        </Box>
    )
}

// Define default props
SubscriptionButton.defaultProps = {
    size: "large",
    onSubscriptionChange: undefined,
    deviceType: undefined,
}

export default SubscriptionButton
